import React, { useEffect, useState } from 'react';
import { Plus, BedDouble, Users, Pencil, X } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const statusColors = {
  AVAILABLE: 'bg-green-100 text-green-700',
  OCCUPIED: 'bg-red-100 text-red-700',
  RESERVED: 'bg-yellow-100 text-yellow-700',
  MAINTENANCE: 'bg-gray-100 text-gray-700',
};

const emptyForm = { roomNumber: '', category: 'STANDARD', capacity: 2, monthlyRent: '', floor: '', description: '', status: 'AVAILABLE' };

export default function Rooms() {
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get('/rooms').then(r => setRooms(r.data)).catch(console.error);

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditRoom(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (room) => {
    setEditRoom(room);
    setForm({
      roomNumber: room.roomNumber,
      category: room.category,
      capacity: room.capacity,
      monthlyRent: Number(room.monthlyRent),
      floor: room.floor || '',
      description: room.description || '',
      status: room.status,
    });
    setShowForm(true);
  };

  const seedRooms = async () => {
    try {
      await api.post('/rooms/seed');
      toast.success('30 rooms created');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const submit = async e => {
    e.preventDefault();
    const payload = { ...form, capacity: Number(form.capacity), monthlyRent: Number(form.monthlyRent), floor: form.floor ? Number(form.floor) : null };
    try {
      if (editRoom) {
        await api.put(`/rooms/${editRoom.id}`, payload);
        toast.success('Room updated');
      } else {
        await api.post('/rooms', payload);
        toast.success('Room created');
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const filtered = filter === 'ALL' ? rooms : rooms.filter(r => r.status === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-bold text-gray-800">Rooms</h2>
        <div className="flex gap-2">
          {rooms.length === 0 && (
            <button onClick={seedRooms} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              Seed 30 Rooms
            </button>
          )}
          <button onClick={openAdd} className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <Plus size={16} /> Add Room
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {['ALL', 'AVAILABLE', 'OCCUPIED', 'RESERVED', 'MAINTENANCE'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === s ? 'bg-purple-700 text-white' : 'bg-white text-gray-600 border'}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {filtered.map(room => (
          <div key={room.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-800">{room.roomNumber}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(room)} className="text-gray-400 hover:text-purple-600 p-0.5">
                  <Pencil size={13} />
                </button>
                <BedDouble size={14} className="text-purple-300" />
              </div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[room.status]}`}>{room.status}</span>
            <p className="text-xs text-gray-500 mt-2">{room.category} · Cap {room.capacity}</p>
            <p className="text-sm font-semibold text-purple-700 mt-1">Rs. {Number(room.monthlyRent).toLocaleString()}</p>
            {room.tenancies?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                {room.tenancies.map(t => (
                  <p key={t.id} className="text-xs text-gray-600 flex items-center gap-1">
                    <Users size={10} /> {t.student.firstName} {t.student.lastName}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">{editRoom ? `Edit Room ${editRoom.roomNumber}` : 'Add Room'}</h3>
              <button onClick={() => setShowForm(false)}><X size={18} className="text-gray-400" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Room No</label>
                  <input required value={form.roomNumber} onChange={e => setForm(p => ({ ...p, roomNumber: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Category</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value, capacity: e.target.value === 'TRIPLE' ? 3 : 2 }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="STANDARD">Standard</option>
                    <option value="TRIPLE">Triple</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Capacity</label>
                  <input type="number" min={1} max={3} required value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Monthly Rent</label>
                  <input type="number" required value={form.monthlyRent} onChange={e => setForm(p => ({ ...p, monthlyRent: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Floor</label>
                  <input type="number" value={form.floor} onChange={e => setForm(p => ({ ...p, floor: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="AVAILABLE">Available</option>
                    <option value="OCCUPIED">Occupied</option>
                    <option value="RESERVED">Reserved</option>
                    <option value="MAINTENANCE">Maintenance</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-purple-700 text-white py-2 rounded-lg text-sm">{editRoom ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
